import { useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Plus, Search, Warehouse } from 'lucide-react';
import { Badge, Button, Card, EmptyState, Input, Skeleton, cn } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { usePermission } from '../../auth/model/usePermission.js';
import { orgApi } from '../../org/api/org.api.js';
import type { WarehouseDto } from '../api/warehouse.api.js';
import { useWarehouses } from '../model/warehouse.queries.js';
import { NewWarehouseDialog } from './NewWarehouseDialog.js';

const area = (warehouse: WarehouseDto): number =>
  Math.round(warehouse.dims.lengthM * warehouse.dims.widthM);

interface WarehouseCardProps {
  warehouse: WarehouseDto;
  orgUnitName: string;
  onOpen: (id: string) => void;
}

const WarehouseCard = ({ warehouse, orgUnitName, onOpen }: WarehouseCardProps) => (
  <Card className="min-w-0 flex-1">
    <div className="flex h-full min-w-0 flex-col gap-3">
    <div className="flex items-start justify-between gap-3">
      <div className="flex min-w-0 flex-col gap-1">
        <h2 className="min-w-0 truncate text-base font-semibold text-text">{warehouse.name}</h2>
        <span className="tabular text-xs text-text-faint">{warehouse.code}</span>
      </div>
      <Badge tone={warehouse.isActive ? 'success' : 'neutral'}>
        {t(warehouse.isActive ? 'warehouse.statusActive' : 'warehouse.statusInactive')}
      </Badge>
    </div>

    <dl className="grid grid-cols-2 gap-3">
      <div className="flex min-w-0 flex-col gap-0.5">
        <dt className="text-xs text-text-faint">{t('warehouse.cardOrgUnit')}</dt>
        <dd className="min-w-0 truncate text-sm text-text">{orgUnitName}</dd>
      </div>
      <div className="flex flex-col gap-0.5">
        <dt className="text-xs text-text-faint">{t('warehouse.cardArea')}</dt>
        <dd className="tabular text-sm text-text">{t('warehouse.areaValue', { area: area(warehouse) })}</dd>
      </div>
      <div className="flex flex-col gap-0.5">
        <dt className="text-xs text-text-faint">{t('warehouse.cardDims')}</dt>
        <dd className="tabular text-sm text-text">
          {`${warehouse.dims.lengthM} × ${warehouse.dims.widthM} × ${warehouse.dims.heightM} m`}
        </dd>
      </div>
      <div className="flex flex-col gap-0.5">
        <dt className="text-xs text-text-faint">{t('warehouse.cardFill')}</dt>
        {/* To'lganlik `stock` kontekstidan keladi (keyingi reja): `GET /warehouses`
            javobida katak sanog'i ham, qoldiq ham YO'Q. Soxta chizig'i chizilgandan
            ko'ra chiziqcha qo'yiladi — raqamga ishonib qaror qilinmasin. */}
        <dd className="flex flex-col">
          <span aria-hidden className="tabular text-sm text-text-faint">
            —
          </span>
          <span className="text-xs text-text-faint">{t('warehouse.cardFillPending')}</span>
        </dd>
      </div>
    </dl>

    {warehouse.address === null ? null : (
      <p className="min-w-0 truncate text-xs text-text-muted">{warehouse.address}</p>
    )}

    <div className="mt-auto pt-1">
      <Button variant="secondary" size="sm" onClick={() => onOpen(warehouse.id)}>
        {t('warehouse.open')}
      </Button>
    </div>
    </div>
  </Card>
);

export interface WarehousesPageProps {
  /** Router `navigate` ustidagi yupqa qobiq — sahifa marshrutlashni bilmaydi. */
  onOpenWarehouse: (id: string) => void;
  /**
   * Butun doira bo'ylab mahsulot qidiruvi. Berilmasa tugma chizilmaydi.
   *
   * Bu YO'QOLMASLIGI kerak bo'lgan ish yo'li: skladchi ba'zan omborni emas,
   * mahsulotni biladi — "bu bolt umuman qayerda bor?" degan savol ombor
   * tanlanishidan OLDIN tug'iladi.
   */
  onSearchStock?: () => void;
}

/**
 * Ombor ro'yxati — kartochka to'ri.
 *
 * Korxona nomi alohida so'rovdan (`GET /org-units/:id`) olinadi: ombor
 * javobida faqat `orgUnitId` bor va identifikatorni ekranga chiqarish odamga
 * hech narsa aytmaydi.
 */
export const WarehousesPage = ({ onOpenWarehouse, onSearchStock }: WarehousesPageProps) => {
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const { canAnywhere } = usePermission();

  const warehouses = useWarehouses(search === '' ? {} : { search });

  const items = useMemo(() => warehouses.data?.items ?? [], [warehouses.data]);

  /*
   * Korxona nomi HAR BIR ombor uchun alohida so'raladi, umumiy ro'yxatdan
   * emas: tuzilmada 500 dan ortiq birlik bor, `GET /org-units` esa bir
   * so'rovda ko'pi bilan 200 ta qaytaradi. Umumiy ro'yxat ishlatilganda
   * ro'yxatga tushmagan korxonaning o'rnida raqam ("23") chiqib qolardi.
   * Bir sahifada omborlar soni kam, javoblar esa `staleTime` bilan
   * keshlanadi — takroriy so'rov ketmaydi.
   */
  const orgUnitIds = useMemo(
    () => [...new Set(items.map((warehouse) => warehouse.orgUnitId))],
    [items],
  );

  const orgUnitQueries = useQueries({
    queries: orgUnitIds.map((id) => ({
      queryKey: ['org', 'unit', id],
      queryFn: () => orgApi.getUnit(id),
      staleTime: 300_000,
    })),
  });

  const orgUnitNames = useMemo(
    () =>
      new Map(
        orgUnitQueries.flatMap((query) =>
          query.data === undefined ? [] : [[query.data.id, query.data.name] as const],
        ),
      ),
    [orgUnitQueries],
  );

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t('warehouse.listTitle')}
        description={t('warehouse.listSubtitle')}
        filters={
          <Input
            variant="filter"
            label={t('warehouse.searchLabel')}
            placeholder={t('warehouse.searchPlaceholder')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        }
        actions={
          <>
            {onSearchStock === undefined ? null : (
              <Button variant="secondary" onClick={onSearchStock}>
                <Search size={16} aria-hidden />
                {t('warehouse.searchAllStock')}
              </Button>
            )}

            {/* Ombor ochish — MTU aktlanish bo'limining ishi. Tugma ruxsatsiz
                odamga umuman chizilmaydi; haqiqiy qaror baribir backendda. */}
            {canAnywhere('warehouse.create') ? (
              <Button onClick={() => setCreating(true)}>
                <Plus size={16} aria-hidden />
                {t('warehouse.newButton')}
              </Button>
            ) : null}
          </>
        }
      />

      {warehouses.isPending ? (
        <div className={cn('grid gap-4 md:grid-cols-2 xl:grid-cols-3')}>
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : warehouses.isError ? (
        <Card>
          <EmptyState
            title={t('warehouse.loadError')}
            description={
              warehouses.error instanceof ApiClientError ? warehouses.error.message : undefined
            }
            action={
              <Button variant="secondary" onClick={() => void warehouses.refetch()}>
                {t('warehouse.retry')}
              </Button>
            }
          />
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Warehouse size={20} aria-hidden />}
            title={t('warehouse.listEmpty')}
            description={t('warehouse.listEmptyHint')}
          />
        </Card>
      ) : (
        <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {items.map((warehouse) => (
            <li key={warehouse.id} className="flex min-w-0">
              <WarehouseCard
                warehouse={warehouse}
                orgUnitName={orgUnitNames.get(warehouse.orgUnitId) ?? '—'}
                onOpen={onOpenWarehouse}
              />
            </li>
          ))}
        </ul>
      )}

      <NewWarehouseDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => void warehouses.refetch()}
      />
    </div>
  );
};
